import { Download } from "lucide-react";
import {
  exportPastChats,
  PastChatsExport,
} from "@/components/Views/pastChatsExport";
import { ConversationsService } from "@/generated";
import { P } from "@/constants/permissions";
import { usePermissions } from "@/hooks/usePermissions";
import { type GenieTool } from "../registries/tool-registry";
import { useGenieTools } from "../use-genie-tools";

const optionalText = (value: unknown): string | undefined =>
  typeof value === "string" && value ? value : undefined;

/**
 * Hands the user the same CSV the Past chats page exports, saved in their
 * browser: the export streams a file, which a backend tool could only describe.
 */
export function pastChatsTools(groupId?: string): GenieTool[] {
  return [
    {
      name: "download_past_chats",
      description:
        "Download past chats as a CSV file, saved in the user's browser, as the Past chats page's Export does. " +
        "kind 'conversations' has one row per chat (title, agent, dates, message and feedback counts, the messages); " +
        "kind 'feedback' has one row per rated reply. Narrow it with agent_id, since/until (ISO 8601) and rated_only. " +
        "A member who may not edit agents gets only their own chats.",
      parameters: {
        type: "object",
        properties: {
          kind: {
            type: "string",
            enum: Object.values(PastChatsExport),
            description: "What each row is: a chat, or a rated reply.",
          },
          agent_id: {
            type: "string",
            description: "Only this agent's chats.",
          },
          since: {
            type: "string",
            description: "Only chats started at or after this time.",
          },
          until: {
            type: "string",
            description: "Only chats started before this time.",
          },
          rated_only: {
            type: "boolean",
            description: "Only chats with a rated reply.",
          },
        },
        required: ["kind"],
      },
      execute: async (input) => {
        if (!groupId) throw new Error("No workspace is open.");
        const scope = {
          assistant_id: optionalText(input.agent_id),
          from_date: optionalText(input.since),
          to_date: optionalText(input.until),
          has_feedback: input.rated_only === true,
        };
        // As the page's Export does: the export takes the list's total for the
        // same filters, which also lets it run with no filter at all.
        const { data: listed } = await ConversationsService.listConversations({
          path: { group_id: groupId },
          query: {
            ...scope,
            has_feedback: scope.has_feedback || undefined,
            page: 1,
            size: 1,
          },
        });
        const kind =
          input.kind === PastChatsExport.Feedback
            ? PastChatsExport.Feedback
            : PastChatsExport.Conversations;
        await exportPastChats(groupId, kind, {
          ...scope,
          expected_count: listed?.total,
        });
        return {
          saved_to_downloads: `${kind}.csv`,
          note: "The file is already in the user's browser downloads. Say so by its name; it has no link or path, so never write one.",
        };
      },
      display: {
        icon: Download,
        messageStart: () => "Downloading past chats...",
        messageEnd: () => "Past chats downloaded",
        messageError: "Download failed",
      },
    },
  ];
}

export function usePastChatsTools(groupId?: string) {
  const { hasAnyPermission } = usePermissions();
  // The Past chats page offers its Export to the same people.
  const canExport = hasAnyPermission(
    [P.RESOURCE_READ, P.RESOURCE_EDIT],
    "group"
  );
  useGenieTools(
    () => (canExport ? pastChatsTools(groupId) : []),
    [groupId, canExport]
  );
}
